import React, { useState, useEffect, useCallback, useMemo } from 'react';
import api from '../../services/api';
import FeePlatformGroup from './FeePlatformGroup';
import FeeCopyFromModal from './FeeCopyFromModal';
import FeeCalculatorFootnotes from './FeeCalculatorFootnotes';
import { MULTIPLIER_KEY, EDITABLE_FIELDS, COPY_FIELDS, computeFeeTotals } from './feeUtils';

// Fee grid indexed off the portfolio registry / mapping: series pills along
// the top, rows grouped by platform, one row per portfolio code. Rows are
// pre-sorted and enriched (platform/series/risk) by the backend.
const FeeCalculatorEditor = () => {
  const [rows, setRows] = useState([]);
  const [originalRows, setOriginalRows] = useState([]);
  const [seriesOptions, setSeriesOptions] = useState([]);
  const [multiplier, setMultiplier] = useState(0.06);
  const [footnotes, setFootnotes] = useState([]);
  const [originalFootnotes, setOriginalFootnotes] = useState([]);
  const [seriesFilter, setSeriesFilter] = useState('all');
  const [collapsedPlatforms, setCollapsedPlatforms] = useState(() => new Set());
  const [copyTargetCode, setCopyTargetCode] = useState(null);
  const [editingMultiplier, setEditingMultiplier] = useState(null);
  const [savingConfig, setSavingConfig] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [message, setMessage] = useState(null);

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    setMessage(null);
    try {
      const [gridRes, footnotesRes] = await Promise.all([
        api.get('/api/admin/portfolio-fees'),
        api.get('/api/admin/portfolio-fees/footnotes'),
      ]);
      setRows(gridRes.data.rows);
      setOriginalRows(JSON.parse(JSON.stringify(gridRes.data.rows)));
      setSeriesOptions(gridRes.data.series_options);
      setMultiplier(gridRes.data.config?.[MULTIPLIER_KEY] ?? 0.06);
      setFootnotes(footnotesRes.data);
      setOriginalFootnotes(JSON.parse(JSON.stringify(footnotesRes.data)));
    } catch (err) {
      console.error('Error fetching portfolio fee data:', err);
      setMessage({ type: 'error', text: 'Failed to load fee data' });
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const originalByCode = useMemo(() => {
    const map = {};
    originalRows.forEach(r => { map[r.portfolio_code] = r; });
    return map;
  }, [originalRows]);

  const isRowChanged = useCallback((row) => {
    const orig = originalByCode[row.portfolio_code];
    if (!orig) return true;
    return EDITABLE_FIELDS.some(f => row[f] !== orig[f]);
  }, [originalByCode]);

  const hasChanges = () =>
    rows.some(r => isRowChanged(r)) ||
    JSON.stringify(footnotes) !== JSON.stringify(originalFootnotes);

  const handleRowChange = (code, field, value) => {
    setRows(prev => prev.map(r => (r.portfolio_code === code ? { ...r, [field]: value } : r)));
  };

  const handleToggleAvailable = (code) => {
    setRows(prev => prev.map(r => (r.portfolio_code === code ? { ...r, is_available: !r.is_available } : r)));
  };

  const handleCopyFrom = (sourceRow) => {
    setRows(prev => prev.map(r => {
      if (r.portfolio_code !== copyTargetCode) return r;
      const updated = { ...r };
      COPY_FIELDS.forEach(f => { updated[f] = sourceRow[f]; });
      return updated;
    }));
    setCopyTargetCode(null);
  };

  const handleSaveAll = async () => {
    setIsSaving(true);
    setMessage(null);
    try {
      const changedRows = rows.filter(r => isRowChanged(r));
      const promises = [];

      if (changedRows.length > 0) {
        promises.push(api.put('/api/admin/portfolio-fees/bulk', {
          items: changedRows.map(r => ({
            portfolio_code: r.portfolio_code,
            is_available: r.is_available,
            pds_date: r.pds_date || null,
            investment_mgmt_fee: r.investment_mgmt_fee,
            sub_portfolio_mgmt_fee: r.sub_portfolio_mgmt_fee,
            indirect_costs: r.indirect_costs,
            aggregate_rebates: r.aggregate_rebates,
            transaction_fees: r.transaction_fees,
            performance_fees: r.performance_fees,
          })),
        }));
      }

      if (JSON.stringify(footnotes) !== JSON.stringify(originalFootnotes)) {
        promises.push(api.put('/api/admin/portfolio-fees/footnotes/bulk', {
          items: footnotes.map((fn, i) => ({
            id: fn.id || null,
            term: fn.term,
            definition: fn.definition,
            sort_order: i,
          })),
        }));
      }

      await Promise.all(promises);
      setMessage({ type: 'success', text: 'Changes saved successfully' });
      fetchData();
    } catch (err) {
      console.error('Error saving portfolio fee data:', err);
      setMessage({ type: 'error', text: err.response?.data?.detail || 'Failed to save changes' });
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveMultiplier = async () => {
    if (editingMultiplier == null) return;
    setSavingConfig(true);
    try {
      await api.put(`/api/admin/portfolio-fees/config/${MULTIPLIER_KEY}`, {
        value: parseFloat(editingMultiplier),
      });
      setMultiplier(parseFloat(editingMultiplier));
      setEditingMultiplier(null);
    } catch (err) {
      console.error('Error saving config:', err);
      setMessage({ type: 'error', text: 'Failed to save multiplier' });
    } finally {
      setSavingConfig(false);
    }
  };

  const handleAddFootnote = () => {
    setFootnotes(prev => [...prev, { id: null, term: '', definition: '', sort_order: prev.length }]);
  };

  const handleRemoveFootnote = (index) => {
    setFootnotes(prev => prev.filter((_, i) => i !== index));
  };

  const togglePlatform = (platform) => {
    setCollapsedPlatforms(prev => {
      const next = new Set(prev);
      if (next.has(platform)) next.delete(platform);
      else next.add(platform);
      return next;
    });
  };

  const seriesCounts = useMemo(() => {
    const counts = {};
    rows.forEach(r => { counts[r.series] = (counts[r.series] || 0) + 1; });
    return counts;
  }, [rows]);

  // Rows arrive pre-sorted; bucket the filtered set by platform preserving order.
  const platformGroups = useMemo(() => {
    const filtered = seriesFilter === 'all' ? rows : rows.filter(r => r.series === seriesFilter);
    const groups = [];
    const index = {};
    filtered.forEach(r => {
      const label = r.platform || 'No Platform';
      if (!(label in index)) {
        index[label] = groups.length;
        groups.push({ platform: label, rows: [] });
      }
      groups[index[label]].rows.push(r);
    });
    return groups;
  }, [rows, seriesFilter]);

  const dirtyCount = rows.filter(r => isRowChanged(r)).length;
  const copyTargetRow = copyTargetCode ? rows.find(r => r.portfolio_code === copyTargetCode) : null;

  if (isLoading) {
    return (
      <div className="card">
        <div className="card-header" style={{ backgroundColor: '#072040', color: '#fff' }}>
          <h5 className="mb-0">PDS Fee Calculator</h5>
        </div>
        <div className="card-body text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-2 text-muted">Loading fee data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="card">
      <div className="card-header d-flex justify-content-between align-items-center" style={{ backgroundColor: '#072040', color: '#fff' }}>
        <h5 className="mb-0">PDS Fee Calculator</h5>
        <button
          className="btn btn-sm"
          style={{ backgroundColor: '#f0a500', color: '#072040', fontWeight: 600 }}
          onClick={handleSaveAll}
          disabled={isSaving || !hasChanges()}
        >
          {isSaving ? (
            <>
              <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
              Saving...
            </>
          ) : (
            `Save All${dirtyCount > 0 ? ` (${dirtyCount})` : ''}`
          )}
        </button>
      </div>

      <div className="card-body">
        {message && (
          <div className={`alert alert-${message.type === 'error' ? 'danger' : 'success'} alert-dismissible fade show`}>
            {message.text}
            <button type="button" className="btn-close" onClick={() => setMessage(null)}></button>
          </div>
        )}

        {/* Series pills */}
        <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
          <button
            className="btn btn-sm rounded-pill"
            style={seriesFilter === 'all'
              ? { backgroundColor: '#072040', color: '#fff' }
              : { border: '1px solid #072040', color: '#072040' }}
            onClick={() => setSeriesFilter('all')}
          >
            All ({rows.length})
          </button>
          {seriesOptions.map(s => (
            <button
              key={s}
              className="btn btn-sm rounded-pill"
              style={seriesFilter === s
                ? { backgroundColor: '#072040', color: '#fff' }
                : { border: '1px solid #072040', color: '#072040' }}
              onClick={() => setSeriesFilter(s)}
            >
              {s} ({seriesCounts[s] || 0})
            </button>
          ))}
        </div>

        {/* Sub-portfolio multiplier config + registry legend */}
        <div className="d-flex flex-wrap align-items-center gap-2 mb-4" style={{ fontSize: '0.85rem' }}>
          <span className="me-3">
            <span style={{ color: '#f0a500' }}>★</span>
            <span className="text-muted"> Portfolio Registry entry ({rows.filter(r => r.in_registry).length}) — canonical portfolios, keep these accurate</span>
          </span>
          <span className="text-muted">
            Sub-Portfolio Multiplier (applied to sub-portfolio fees in Total Fixed):
          </span>
          {editingMultiplier == null ? (
            <>
              <span className="badge" style={{ backgroundColor: '#072040', fontSize: '0.85rem' }}>
                {(multiplier * 100).toFixed(1)}%
              </span>
              <button className="btn btn-sm btn-outline-primary py-0" onClick={() => setEditingMultiplier(multiplier)}>
                Edit
              </button>
            </>
          ) : (
            <>
              <input
                type="number"
                className="form-control form-control-sm"
                style={{ maxWidth: '100px' }}
                value={editingMultiplier}
                onChange={(e) => setEditingMultiplier(e.target.value)}
                step="0.01"
                min="0"
                max="1"
              />
              <button className="btn btn-sm btn-primary py-0" onClick={handleSaveMultiplier} disabled={savingConfig}>
                Save
              </button>
              <button className="btn btn-sm btn-outline-secondary py-0" onClick={() => setEditingMultiplier(null)}>
                Cancel
              </button>
            </>
          )}
        </div>

        {/* Platform groups */}
        {platformGroups.map(group => (
          <FeePlatformGroup
            key={group.platform}
            platform={group.platform}
            rows={group.rows}
            collapsed={collapsedPlatforms.has(group.platform)}
            onToggle={() => togglePlatform(group.platform)}
            multiplier={multiplier}
            isRowChanged={isRowChanged}
            onChange={handleRowChange}
            onToggleAvailable={handleToggleAvailable}
            onCopyClick={setCopyTargetCode}
          />
        ))}

        {platformGroups.length === 0 && (
          <p className="text-muted">No portfolios found{seriesFilter !== 'all' ? ` in ${seriesFilter}` : ''}.</p>
        )}

        <FeeCalculatorFootnotes
          footnotes={footnotes}
          onChange={setFootnotes}
          onAdd={handleAddFootnote}
          onRemove={handleRemoveFootnote}
        />
      </div>

      {copyTargetRow && (
        <FeeCopyFromModal
          targetRow={copyTargetRow}
          rows={rows}
          multiplier={multiplier}
          onSelect={handleCopyFrom}
          onClose={() => setCopyTargetCode(null)}
        />
      )}
    </div>
  );
};

export default FeeCalculatorEditor;
